import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, DollarSign, Settings } from "react-feather";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AddEdit_RateMaster = () => {
  const API_URL_SAVE = "RateMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/RateMaster/Id";
  const API_URL_VEHICLE = `${API_WEB_URLS.MASTER}/0/token/VehicleMaster/Id/0`;
  const API_URL_SPECIAL_OCCASION = `${API_WEB_URLS.MASTER}/0/token/SpecialOccasionMaster/Id/0`;
  const API_URL_DISCOUNT = `${API_WEB_URLS.MASTER}/0/token/DiscountMaster/Id/0`;
  
  const breadCrumbTitle = "Rate Master";
  const breadcrumbItem = "Rate Master";

  const [state, setState] = useState({
    id: 0,
    formData: { 
      F_VehicleMaster: "",
      DistanceInKM: "",
      Time: "",
      Rate: "",
      Discount: "",
      IsNight: false,
      F_SpecialOccasion: "",
      F_DiscountMaster: "",
    }
  });

  const [vehicleList, setVehicleList] = useState([]);
  const [specialOccasionList, setSpecialOccasionList] = useState([]);
  const [discountList, setDiscountList] = useState([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const vehicleRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;

    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Fetch Vehicle, Special Occasion, and Discount lists
  useEffect(() => {
    Fn_FillListData(dispatch, setVehicleList, "gridData", API_URL_VEHICLE);
    Fn_FillListData(dispatch, setSpecialOccasionList, "gridData", API_URL_SPECIAL_OCCASION);
    Fn_FillListData(dispatch, setDiscountList, "gridData", API_URL_DISCOUNT);
  }, [dispatch]);

  // Auto-focus on Vehicle field when component mounts
  useEffect(() => {
    if (vehicleRef.current) {
      vehicleRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    F_VehicleMaster: Yup.string()
      .required("Vehicle is required"),
    DistanceInKM: Yup.number()
      .required("Distance is required")
      .min(0.01, "Distance must be greater than 0")
      .max(999999, "Distance must be less than 999,999"),
    Time: Yup.number()
      .required("Time is required")
      .min(0.01, "Time must be greater than 0")
      .max(999, "Time must be less than 999"),
    Rate: Yup.number()
      .required("Rate is required")
      .min(0, "Rate must be 0 or greater")
      .max(999999, "Rate must be less than 999,999"),
    Discount: Yup.number()
      .min(0, "Discount must be 0 or greater")
      .max(100, "Discount cannot exceed 100%"),
    F_SpecialOccasion: Yup.string(),
    F_DiscountMaster: Yup.string(),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("F_VehicleMaster", values.F_VehicleMaster || "");
    formData.append("DistanceInKM", values.DistanceInKM || "");
    formData.append("Time", values.Time || "");
    formData.append("Rate", values.Rate || "");
    formData.append("Discount", values.Discount || "");
    formData.append("IsNight", values.IsNight || false);
    formData.append("F_SpecialOccasion", values.F_SpecialOccasion || "");
    formData.append("F_DiscountMaster", values.F_DiscountMaster || "");
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "rateid",
      navigate,
      "/RateMaster"
    );
  };

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header" style={{padding: '12px 20px'}}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={18} color="#6D68CB" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold">Rate Master</h5>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="master-form-card-body">
                <Formik 
                  initialValues={{
                    F_VehicleMaster: state.formData.F_VehicleMaster || "",
                    DistanceInKM: state.formData.DistanceInKM || "",
                    Time: state.formData.Time || "",
                    Rate: state.formData.Rate || "",
                    Discount: state.formData.Discount || "",
                    IsNight: state.formData.IsNight || false,
                    F_SpecialOccasion: state.formData.F_SpecialOccasion || "",
                    F_DiscountMaster: state.formData.F_DiscountMaster || "",
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="F_VehicleMaster" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Vehicle <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              innerRef={vehicleRef}
                              as="select"
                              className={`master-form-input ${errors.F_VehicleMaster && touched.F_VehicleMaster ? 'is-invalid' : ''}`}
                              name="F_VehicleMaster"
                            >
                              <option value="">Select Vehicle</option>
                              {vehicleList && vehicleList.map((vehicle) => (
                                <option key={vehicle.Id} value={vehicle.Id}>
                                  {vehicle.VehicleNumber} - {vehicle.Brand} {vehicle.Model}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_VehicleMaster" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select the vehicle for which rate is being set
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="DistanceInKM" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Distance (KM) <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.DistanceInKM && touched.DistanceInKM ? 'is-invalid' : ''}`}
                              type="number"
                              name="DistanceInKM"
                              placeholder="Enter distance in kilometers"
                              step="0.01"
                              min="0"
                            />
                            <div className="input-icon">
                              <DollarSign size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="DistanceInKM" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the distance for the rate calculation
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Time" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Time (Hours) <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Time && touched.Time ? 'is-invalid' : ''}`}
                              type="number"
                              name="Time"
                              placeholder="Enter time in hours"
                              step="0.01"
                              min="0"
                            />
                            <div className="input-icon">
                              <DollarSign size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Time" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the time duration for the rate
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Rate" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Rate (₹) <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Rate && touched.Rate ? 'is-invalid' : ''}`}
                              type="number"
                              name="Rate"
                              placeholder="Enter rate amount"
                              step="0.01"
                              min="0"
                            />
                            <div className="input-icon">
                              <DollarSign size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Rate" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the rate amount in rupees
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Discount" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Discount (%) <span className="text-muted">(Optional)</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Discount && touched.Discount ? 'is-invalid' : ''}`}
                              type="number"
                              name="Discount"
                              placeholder="Enter discount percentage"
                              step="0.01"
                              min="0"
                              max="100"
                            />
                            <div className="input-icon">
                              <DollarSign size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Discount" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter discount percentage (0-100)
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="IsNight" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Night Service <span className="text-muted">(Optional)</span>
                          </label>
                          <div className="input-wrapper">
                            <div className="form-check form-switch">
                              <Field
                                className="form-check-input"
                                type="checkbox"
                                name="IsNight"
                                id="IsNight"
                              />
                              <label className="form-check-label" htmlFor="IsNight">
                                Enable Night Service
                              </label>
                            </div>
                          </div>
                          <ErrorMessage name="IsNight" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Check if this rate applies to night services
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_SpecialOccasion" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Special Occasion <span className="text-muted">(Optional)</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_SpecialOccasion && touched.F_SpecialOccasion ? 'is-invalid' : ''}`}
                              name="F_SpecialOccasion"
                            >
                              <option value="">Select Special Occasion</option>
                              {specialOccasionList && specialOccasionList.map((occasion) => (
                                <option key={occasion.Id} value={occasion.Id}>
                                  {occasion.Name}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_SpecialOccasion" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select if this rate applies to special occasions
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_DiscountMaster" className="master-form-label">
                            <DollarSign size={16} className="me-2" />
                            Discount Master <span className="text-muted">(Optional)</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_DiscountMaster && touched.F_DiscountMaster ? 'is-invalid' : ''}`}
                              name="F_DiscountMaster"
                            >
                              <option value="">Select Discount Master</option>
                              {discountList && discountList.map((discount) => (
                                <option key={discount.Id} value={discount.Id}>
                                  {discount.Name}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_DiscountMaster" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select applicable discount master
                          </div>
                        </div>
                      </div>

                      <div className="master-form-actions mt-3">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate("/RateMaster")}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            {state.id > 0 ? 'Update Rate' : 'Save Rate'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_RateMaster;
